/**
 * Guided photo capture for the device inspection (2026-10-10).
 *
 * - **Serial label first**, as a wide tile: the one photo the AI rig reads
 *   (psar01), and the one completion needs — unless staff choose **No visible
 *   serial**, which sets the inspection's `serialNotLegible` instead.
 * - **Suggested shots** for the device type (`INSPECTION_SCHEMA.photoShots`),
 *   one tile each, with coverage shown but never enforced (owner's decision:
 *   guided, not required).
 * - **Areas of interest**: any number of close-ups, each with a short
 *   description ("Cracked left hinge") saved as the photo's caption — what the
 *   condition report prints under it.
 * - Photos from before shots existed show under "Earlier photos", delete-only.
 *
 * Every shot but an area of interest holds one photo; a retake replaces it
 * server-side. Tap a photo for the lightbox, the bin to delete (confirmed in
 * DeletePhotoDialog). Camera by default; Gallery for a photo already on the
 * phone. Styling: `.ci-shot*` / `.ci-frame*` in checkInDevice.css.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { IconCamera, IconCheck, IconDownload, IconPhoto, IconPlus, IconRefresh, IconTrash } from '@tabler/icons-react';
import type { CheckInPhoto, CheckInPhotoShot } from '@techgitter/contracts';

import {
  deleteCheckInPhoto,
  updateCheckInPhotoCaption,
  uploadCheckInPhoto,
} from '../../../services/checkInApi';
import type { PhotoShotDef } from '../../../utils/inspectionSchema';
import { Chip } from '../../chip';
import { DeletePhotoDialog } from './DeletePhotoDialog';
import { PhotoLightbox } from './PhotoLightbox';
import { photoDownloadLink } from './photoDownload';

const SERIAL: CheckInPhotoShot = 'serial_label';
const AREA: CheckInPhotoShot = 'area_of_interest';

export interface PhotoCaptureProps {
  sessionId: string;
  draftJobId: string;
  photos: CheckInPhoto[];
  /** The serial tile's name and where-to-look hint for this device type. */
  serialShot: { label: string; hint: string };
  /** Suggested shots after the serial label, in report order. */
  shots: PhotoShotDef[];
  /** "No visible serial" — the inspection's `serialNotLegible`. */
  noVisibleSerial: boolean;
  onNoVisibleSerialChange: (value: boolean) => void;
  /** View only: no capture, no delete (a cancelled or expired check-in). */
  readOnly?: boolean;
  onPhotosChanged: () => void;
}

interface PendingUpload {
  clientId: string;
  shot: CheckInPhotoShot;
  status: 'uploading' | 'failed';
  errorMessage?: string;
  file: File;
}

export const PhotoCapture: React.FC<PhotoCaptureProps> = ({
  sessionId,
  draftJobId,
  photos,
  serialShot,
  shots,
  noVisibleSerial,
  onNoVisibleSerialChange,
  readOnly = false,
  onPhotosChanged,
}) => {
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const targetShot = useRef<CheckInPhotoShot>(SERIAL);
  const [source, setSource] = useState<'camera' | 'gallery'>('camera');
  const [pending, setPending] = useState<PendingUpload[]>([]);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [photoToDelete, setPhotoToDelete] = useState<CheckInPhoto | null>(null);

  // Grouped once per render: the newest photo per single-photo shot (the
  // server retires older ones on a retake; newest-wins covers the moment
  // before the refetch lands), every area of interest, and the unlabelled rest.
  const offered = useMemo(() => new Set<string>([SERIAL, ...shots.map(s => s.key)]), [shots]);
  const { byShot, areas, earlier } = useMemo(() => {
    const byShotMap = new Map<string, CheckInPhoto>();
    const areaList: CheckInPhoto[] = [];
    const rest: CheckInPhoto[] = [];
    for (const photo of [...photos].sort((a, b) => a.uploadedAt.localeCompare(b.uploadedAt))) {
      if (photo.shot === AREA) areaList.push(photo);
      else if (photo.shot && offered.has(photo.shot)) byShotMap.set(photo.shot, photo);
      else rest.push(photo);
    }
    return { byShot: byShotMap, areas: areaList, earlier: rest };
  }, [photos, offered]);

  // Lightbox order matches the page (and the condition report).
  const ordered = useMemo(
    () => [
      ...[SERIAL, ...shots.map(s => s.key)].map(k => byShot.get(k)).filter((p): p is CheckInPhoto => !!p),
      ...areas,
      ...earlier,
    ],
    [byShot, shots, areas, earlier],
  );
  const openLightbox = (photo: CheckInPhoto) => setLightboxIndex(ordered.findIndex(p => p.id === photo.id));

  const upload = async (entry: PendingUpload) => {
    try {
      await uploadCheckInPhoto(sessionId, draftJobId, entry.file, entry.shot);
      setPending(p => p.filter(x => x.clientId !== entry.clientId));
      // A label photo answers the question "No visible serial" was standing in for.
      if (entry.shot === SERIAL && noVisibleSerial) onNoVisibleSerialChange(false);
      onPhotosChanged();
    } catch (err) {
      setPending(p =>
        p.map(x =>
          x.clientId === entry.clientId
            ? { ...x, status: 'failed', errorMessage: err instanceof Error ? err.message : 'Upload failed' }
            : x,
        ),
      );
    }
  };

  const take = (shot: CheckInPhotoShot) => {
    targetShot.current = shot;
    (source === 'camera' ? cameraInputRef : galleryInputRef).current?.click();
  };

  const handleFile = (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    const shot = targetShot.current;
    const entry: PendingUpload = {
      clientId: `pending-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      shot,
      status: 'uploading',
      file,
    };
    // One pending upload per single-photo shot: a fresh take supersedes a failed one.
    setPending(p => [...p.filter(x => shot === AREA || x.shot !== shot), entry]);
    void upload(entry);
  };

  const retry = (entry: PendingUpload) => {
    const next = { ...entry, status: 'uploading' as const, errorMessage: undefined };
    setPending(p => p.map(x => (x.clientId === entry.clientId ? next : x)));
    void upload(next);
  };

  // Throws on failure so DeletePhotoDialog can show the error and stay open.
  const confirmDelete = async () => {
    if (!photoToDelete) return;
    await deleteCheckInPhoto(sessionId, draftJobId, photoToDelete.id);
    onPhotosChanged();
  };

  const pendingFor = (shot: CheckInPhotoShot) => pending.find(p => p.shot === shot);
  const serialPhoto = byShot.get(SERIAL);
  const serialPending = pendingFor(SERIAL);
  const done = shots.filter(s => byShot.has(s.key)).length;

  return (
    <div className="ci-section">
      {!readOnly && (
        <div className="ci-shot-source" role="radiogroup" aria-label="Take photos with">
          {(['camera', 'gallery'] as const).map(value => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={source === value}
              className={`ci-shot-source__opt${source === value ? ' ci-shot-source__opt--on' : ''}`}
              onClick={() => setSource(value)}
            >
              {value === 'camera' ? <IconCamera size={15} stroke={1.5} /> : <IconPhoto size={15} stroke={1.5} />}
              {value === 'camera' ? 'Camera' : 'Gallery'}
            </button>
          ))}
        </div>
      )}

      <SerialTile
        label={serialShot.label}
        hint={serialShot.hint}
        photo={serialPhoto}
        pending={serialPending}
        noVisibleSerial={noVisibleSerial}
        readOnly={readOnly}
        onTake={() => take(SERIAL)}
        onRetry={() => serialPending && retry(serialPending)}
        onOpen={() => serialPhoto && openLightbox(serialPhoto)}
        onDelete={() => serialPhoto && setPhotoToDelete(serialPhoto)}
        onNoVisibleSerial={onNoVisibleSerialChange}
      />

      {shots.length > 0 && (
        <div className="ci-shot-coverage">
          <div className="ci-shot-coverage__bar" aria-hidden="true">
            <i style={{ width: `${(done / shots.length) * 100}%` }} />
          </div>
          <span className="ci-shot-coverage__text">
            {done} of {shots.length} suggested shots
          </span>
        </div>
      )}

      <div className="ci-shots">
        {shots.map(def => (
          <ShotTile
            key={def.key}
            name={def.label}
            detail={def.detail}
            photo={byShot.get(def.key)}
            pending={pendingFor(def.key)}
            readOnly={readOnly}
            onTake={() => take(def.key)}
            onRetry={retry}
            onOpen={openLightbox}
            onDelete={setPhotoToDelete}
          />
        ))}

        {areas.map((photo, i) => (
          <div className="ci-shot" key={photo.id}>
            <div className="ci-shot__name">
              Area of interest<small>{i + 1}</small>
            </div>
            <Frame photo={photo} readOnly={readOnly} onOpen={openLightbox} onDelete={setPhotoToDelete} />
            <CaptionInput
              photo={photo}
              readOnly={readOnly}
              onSave={caption => updateCheckInPhotoCaption(sessionId, draftJobId, photo.id, caption)}
            />
          </div>
        ))}
        {pending
          .filter(p => p.shot === AREA)
          .map(p => (
            <div className="ci-shot" key={p.clientId}>
              <div className="ci-shot__name">Area of interest</div>
              <PendingFrame entry={p} onRetry={retry} />
            </div>
          ))}
        {!readOnly && (
          <div className="ci-shot">
            <div className="ci-shot__name">
              Area of interest<small>crack, dent, broken hinge…</small>
            </div>
            <button type="button" className="ci-frame ci-frame--empty" onClick={() => take(AREA)} aria-label="Add an area of interest">
              <IconPlus size={20} stroke={1.5} />
              <span>Add</span>
            </button>
          </div>
        )}
      </div>

      {earlier.length > 0 && (
        <>
          <div className="ci-subhead">Earlier photos</div>
          <div className="ci-shots">
            {earlier.map(photo => (
              <div className="ci-shot" key={photo.id}>
                <Frame photo={photo} readOnly={readOnly} onOpen={openLightbox} onDelete={setPhotoToDelete} />
              </div>
            ))}
          </div>
        </>
      )}

      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={e => {
          handleFile(e.target.files);
          e.target.value = '';
        }}
      />
      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={e => {
          handleFile(e.target.files);
          e.target.value = '';
        }}
      />

      {lightboxIndex !== null && lightboxIndex >= 0 && (
        <PhotoLightbox
          photos={ordered}
          startIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onDelete={
            readOnly
              ? undefined
              : photo => {
                  setLightboxIndex(null);
                  setPhotoToDelete(photo);
                }
          }
        />
      )}

      <DeletePhotoDialog
        photo={photoToDelete}
        onClose={() => setPhotoToDelete(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------

const SerialTile: React.FC<{
  label: string;
  hint: string;
  photo?: CheckInPhoto;
  pending?: PendingUpload;
  noVisibleSerial: boolean;
  readOnly: boolean;
  onTake: () => void;
  onRetry: () => void;
  onOpen: () => void;
  onDelete: () => void;
  onNoVisibleSerial: (value: boolean) => void;
}> = ({ label, hint, photo, pending, noVisibleSerial, readOnly, onTake, onRetry, onOpen, onDelete, onNoVisibleSerial }) => {
  const optedOut = noVisibleSerial && !photo && !pending;
  const reading = photo?.aiReading;
  let status: React.ReactNode;
  if (pending?.status === 'uploading') status = <Chip tone="neutral">Uploading…</Chip>;
  else if (pending?.status === 'failed') status = <Chip tone="error">Upload failed</Chip>;
  else if (optedOut) status = <Chip tone="neutral">No visible serial</Chip>;
  else if (!photo) status = readOnly ? <Chip tone="neutral">Not taken</Chip> : <Chip tone="warning">Needed</Chip>;
  else if (reading?.status === 'pending') {
    status = (
      <span className="ci-shot-reading">
        <span className="ci-shot-reading__spinner" aria-hidden="true" />
        Reading the label…
      </span>
    );
  } else if (reading?.outcome === 'read') {
    status = (
      <Chip tone="success">
        <IconCheck size={12} stroke={2} />
        Label read
      </Chip>
    );
  } else if (reading?.outcome === 'no_label') status = <Chip tone="warning">Couldn&apos;t read the label — check the photo</Chip>;
  else status = <Chip tone="success"><IconCheck size={12} stroke={2} />Taken</Chip>;

  const frame = pending ? (
    <PendingFrame entry={pending} onRetry={onRetry} />
  ) : photo ? (
    <Frame photo={photo} readOnly={readOnly} onOpen={onOpen} onDelete={onDelete} />
  ) : readOnly || optedOut ? (
    <div className="ci-frame ci-frame--blank" aria-label="No photo">—</div>
  ) : (
    <button type="button" className="ci-frame ci-frame--empty" onClick={onTake} aria-label={`Take ${label}`}>
      <IconCamera size={20} stroke={1.5} />
      <span>Take</span>
    </button>
  );

  return (
    <div
      className={`ci-shot-serial${photo ? ' ci-shot-serial--done' : ''}${optedOut ? ' ci-shot-serial--optout' : ''}`}
    >
      {frame}
      <div className="ci-shot-serial__main">
        <div className="ci-shot-serial__label">
          {label} {status}
        </div>
        <div className="ci-caption">
          {optedOut
            ? 'Marked as having no visible serial. The Serial field is optional.'
            : hint}
        </div>
        {!readOnly && !pending && (
          <div className="ci-shot-serial__actions">
            {optedOut ? (
              <button type="button" className="ci-yn-row__addnote" onClick={() => onNoVisibleSerial(false)}>
                Undo — take a label photo instead
              </button>
            ) : photo ? (
              <button type="button" className="ci-yn-row__addnote" onClick={onTake}>
                Retake
              </button>
            ) : (
              <>
                <button type="button" className="ci-btn ci-btn--primary" onClick={onTake}>
                  <IconCamera size={16} stroke={1.5} />
                  Take label photo
                </button>
                <button type="button" className="ci-btn ci-btn--ghost" onClick={() => onNoVisibleSerial(true)}>
                  No visible serial
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const ShotTile: React.FC<{
  name: string;
  detail?: string;
  photo?: CheckInPhoto;
  pending?: PendingUpload;
  readOnly: boolean;
  onTake: () => void;
  onRetry: (entry: PendingUpload) => void;
  onOpen: (photo: CheckInPhoto) => void;
  onDelete: (photo: CheckInPhoto) => void;
}> = ({ name, detail, photo, pending, readOnly, onTake, onRetry, onOpen, onDelete }) => (
  <div className="ci-shot">
    <div className="ci-shot__name">
      {name}
      {detail && <small>{detail}</small>}
    </div>
    {pending ? (
      <PendingFrame entry={pending} onRetry={onRetry} />
    ) : photo ? (
      <Frame photo={photo} readOnly={readOnly} onOpen={onOpen} onDelete={onDelete} />
    ) : readOnly ? (
      <div className="ci-frame ci-frame--blank" aria-label="Not taken">—</div>
    ) : (
      <button type="button" className="ci-frame ci-frame--empty" onClick={onTake} aria-label={`Take ${name}${detail ? `, ${detail}` : ''}`}>
        <IconCamera size={20} stroke={1.5} />
        <span>Take</span>
      </button>
    )}
  </div>
);

const Frame: React.FC<{
  photo: CheckInPhoto;
  readOnly: boolean;
  onOpen: (photo: CheckInPhoto) => void;
  onDelete: (photo: CheckInPhoto) => void;
}> = ({ photo, readOnly, onOpen, onDelete }) => {
  const src = photo.signedUrl || photo.blobUrl;
  const download = photoDownloadLink(photo);
  return (
    <div className="ci-frame ci-frame--photo">
      <button
        type="button"
        className="ci-frame__img"
        style={{ backgroundImage: src ? `url(${src})` : 'none' }}
        onClick={() => onOpen(photo)}
        aria-label="View photo"
      />
      {/* Revealed on hover with a mouse, always visible on touch (no hover
          there) — see .ci-frame__dl in checkInDevice.css. Download stays on a
          read-only check-in: saving a copy changes nothing. */}
      {download && (
        <a
          className="ci-frame__dl"
          href={download.href}
          target={download.target}
          rel="noopener noreferrer"
          aria-label="Download photo"
          title="Download"
        >
          <IconDownload size={16} stroke={1.5} />
        </a>
      )}
      {!readOnly && (
        <button type="button" className="ci-frame__del" onClick={() => onDelete(photo)} aria-label="Delete photo">
          <IconTrash size={16} stroke={1.5} />
        </button>
      )}
    </div>
  );
};

const PendingFrame: React.FC<{ entry: PendingUpload; onRetry: (entry: PendingUpload) => void }> = ({ entry, onRetry }) =>
  entry.status === 'uploading' ? (
    <div className="ci-frame ci-frame--blank" aria-label="Uploading">
      <span className="ci-shot-reading__spinner" aria-hidden="true" />
      <span>Uploading</span>
    </div>
  ) : (
    <button
      type="button"
      className="ci-frame ci-frame--failed"
      onClick={() => onRetry(entry)}
      title={entry.errorMessage}
      aria-label="Upload failed — retry"
    >
      <IconRefresh size={18} stroke={1.5} />
      <span>Retry</span>
    </button>
  );

/** An area of interest's description — saved when the field loses focus. */
const CaptionInput: React.FC<{
  photo: CheckInPhoto;
  readOnly: boolean;
  onSave: (caption: string) => Promise<void>;
}> = ({ photo, readOnly, onSave }) => {
  const [value, setValue] = useState(photo.caption ?? '');
  const [saved, setSaved] = useState(photo.caption ?? '');
  const [error, setError] = useState<string | null>(null);

  // A refetch brings the stored caption back; adopt it unless mid-edit.
  useEffect(() => {
    if (value === saved) {
      setValue(photo.caption ?? '');
      setSaved(photo.caption ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photo.caption]);

  if (readOnly) {
    return <div className="ci-shot__caption">{photo.caption || '—'}</div>;
  }
  const save = async () => {
    const next = value.trim();
    if (next === saved.trim()) return;
    try {
      await onSave(next);
      setSaved(next);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Not saved');
    }
  };
  return (
    <>
      <input
        className="ci-shot__caption-input"
        value={value}
        maxLength={200}
        placeholder="What is it?"
        aria-label="Describe this area of interest"
        onChange={e => setValue(e.target.value)}
        onBlur={() => void save()}
        onKeyDown={e => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        }}
      />
      {error && <div className="ci-shot__caption-error">{error}</div>}
    </>
  );
};
